import { useState, useCallback } from 'react';
import { ErrorHandler } from '../utils/error.utils';
import { useNotification } from '../contexts/NotificationContext';

interface UseAsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseAsyncReturn<T> {
  state: UseAsyncState<T>;
  execute: (...args: any[]) => Promise<T | null>;
  reset: () => void;
  setData: (data: T | null) => void;
}

export const useAsync = <T = any>(
  asyncFunction: (...args: any[]) => Promise<T>,
  showNotifications = true
): UseAsyncReturn<T> => {
  const [state, setState] = useState<UseAsyncState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const { error: showError, success: showSuccess } = useNotification();

  const execute = useCallback(
    async (...args: any[]): Promise<T | null> => {
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        const data = await asyncFunction(...args);
        setState({ data, loading: false, error: null });
        return data;
      } catch (error) {
        const errorMessage = ErrorHandler.handle(error);
        setState(prev => ({ ...prev, loading: false, error: errorMessage }));
        
        if (showNotifications) {
          showError('Error', errorMessage);
        }
        
        return null;
      }
    },
    [asyncFunction, showError, showNotifications]
  );

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  const setData = useCallback((data: T | null) => {
    setState(prev => ({ ...prev, data }));
  }, []);

  return { state, execute, reset, setData };
};

export const useAsyncCallback = <T = any>(
  asyncFunction: (...args: any[]) => Promise<T>,
  successMessage?: string,
  showNotifications = true
) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { error: showError, success: showSuccess } = useNotification();

  const execute = useCallback(
    async (...args: any[]): Promise<T | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await asyncFunction(...args);
        
        if (showNotifications && successMessage) {
          showSuccess('Éxito', successMessage);
        }
        
        return result;
      } catch (error) {
        const errorMessage = ErrorHandler.handle(error);
        setError(errorMessage);
        
        if (showNotifications) {
          showError('Error', errorMessage);
        }
        
        return null;
      } finally {
        setLoading(false);
      }
    },
    [asyncFunction, successMessage, showError, showSuccess, showNotifications]
  );

  return { execute, loading, error };
};
